import { useCurrentUser } from 'FrontRoyalAngular';
import { useProjectsForPlaylists } from 'LearnerProjects';
import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';
import { useUnmount } from 'react-use';
import { getPendingFocusedProjectRequirementIdentifier, studentDashboardActions } from 'StudentDashboardSlice';
import { getActivePlaylistLocalePackId, getCohort, userActionsApi } from 'Users';
import { useQueuedSearchParams } from 'NavigationHelpers';

const { useSetActivePlaylistMutation } = userActionsApi;

// oxlint-disable-next-line max-lines-per-function
export function useFocusProject() {
    const isCurrentlyStudyingRoute = !!useMatch('/dashboard/currently-studying');
    const { searchParams, queueSearchParamsUpdate } = useQueuedSearchParams();
    const currentUser = useCurrentUser();
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);
    const { projectsByPlaylist } = useProjectsForPlaylists();
    const dispatch = useDispatch();
    const projectRequirementIdentifier =
        useSelector(getPendingFocusedProjectRequirementIdentifier) || searchParams.get('projectRequirementIdentifier');
    const willBeViewingProjectFeedback = searchParams.get('viewProjectFeedback') === 'true';
    const [setActivePlaylist] = useSetActivePlaylistMutation();
    const activePlaylistLocalePackId = useMemo(() => getActivePlaylistLocalePackId(currentUser), [currentUser]);

    const playlistLocalePackId = useMemo(() => {
        if (!cohort) return null;
        const _playlistLocalePackId = Object.keys(projectsByPlaylist).find(lpid =>
            projectsByPlaylist[lpid].some(p => p.requirementIdentifier === projectRequirementIdentifier),
        );
        return _playlistLocalePackId ?? null;
    }, [cohort, projectsByPlaylist, projectRequirementIdentifier]);

    const projectId = useMemo(() => {
        if (!playlistLocalePackId) return null;
        return (
            projectsByPlaylist[playlistLocalePackId]?.find(
                p => p.requirementIdentifier === projectRequirementIdentifier,
            )?.id ?? null
        );
    }, [playlistLocalePackId, projectRequirementIdentifier, projectsByPlaylist]);

    // Set the project to focus on, and set the active playlist for the user and their UPS
    useEffect(() => {
        if (
            !isCurrentlyStudyingRoute ||
            !projectRequirementIdentifier ||
            !cohort?.programType ||
            !playlistLocalePackId
        ) {
            return;
        }

        dispatch(
            studentDashboardActions.setPendingFocusedProjectRequirementIdentifier({
                requirementIdentifier: projectRequirementIdentifier,
            }),
        );

        if (playlistLocalePackId === activePlaylistLocalePackId) return;

        setActivePlaylist({
            program_type: cohort.programType,
            playlist_locale_pack_id: playlistLocalePackId,
        });
    }, [
        isCurrentlyStudyingRoute,
        projectRequirementIdentifier,
        cohort?.programType,
        playlistLocalePackId,
        activePlaylistLocalePackId,
        dispatch,
        setActivePlaylist,
    ]);

    const clearPendingFocusedProject = useCallback(() => {
        // remove the project requirement identifier from the search params
        queueSearchParamsUpdate(prev => {
            prev.delete('projectRequirementIdentifier');
            return prev;
        });
        dispatch(
            studentDashboardActions.setPendingFocusedProjectRequirementIdentifier({
                requirementIdentifier: null,
            }),
        );
    }, [queueSearchParamsUpdate, dispatch]);

    const setPendingViewProjectFeedback = useCallback(() => {
        if (!projectRequirementIdentifier || !willBeViewingProjectFeedback) return;

        queueSearchParamsUpdate(prev => {
            prev.delete('viewProjectFeedback');
            return prev;
        });
        dispatch(
            studentDashboardActions.setPendingViewProjectFeedbackRequirementIdentifier({
                requirementIdentifier: projectRequirementIdentifier,
            }),
        );
    }, [queueSearchParamsUpdate, dispatch, projectRequirementIdentifier, willBeViewingProjectFeedback]);

    // Wait for the project to be rendered and scroll to it
    useEffect(() => {
        if (!projectRequirementIdentifier || !projectId || !isCurrentlyStudyingRoute) return () => {};

        const startTime = Date.now();
        const timeoutMs = 20000; // Waiting limit while playlist is changed and route is rendered
        let rafId: number | null = null;

        const waitForElementAndScroll = () => {
            const escapedIdentifier = CSS.escape(projectRequirementIdentifier);
            const elements = document.querySelectorAll<HTMLDivElement>(`.currently-studying .${escapedIdentifier}`);
            // We render Courseware separately for mobile and desktop, so there may be two
            // elements for the same project. Select the one that is visible.
            const visibleElement = Array.from(elements).find(element => element.offsetParent !== null);
            if (visibleElement) {
                visibleElement.scrollIntoView({
                    // with the modal overlaying, "instant" is less distracting; otherwise "smooth" looks better
                    behavior: willBeViewingProjectFeedback ? 'instant' : 'smooth',
                    block: 'center',
                });
                dispatch(studentDashboardActions.forceExpandContentItem({ itemId: projectId }));
                setPendingViewProjectFeedback();
                clearPendingFocusedProject();
            } else if (Date.now() - startTime < timeoutMs) {
                rafId = requestAnimationFrame(waitForElementAndScroll);
            } else {
                // Timeout reached - clear pending focus
                clearPendingFocusedProject();
            }
        };
        rafId = requestAnimationFrame(waitForElementAndScroll);

        // Cleanup function to cancel animation frame
        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [
        projectRequirementIdentifier,
        dispatch,
        projectId,
        isCurrentlyStudyingRoute,
        clearPendingFocusedProject,
        setPendingViewProjectFeedback,
        willBeViewingProjectFeedback,
    ]);

    useUnmount(() => clearPendingFocusedProject());
}
